// Liquid templating for value/expression resolution (shared model with the server
// renderer so editor preview == API output). Cells/free-text use {{ ... | filters }};
// layout/repetition stays in the editor + renderer, never {% for %}.
import { Liquid } from 'liquidjs'

const toNums = (arr: any, key?: string): number[] =>
  (Array.isArray(arr) ? arr : []).map((o) => Number(key ? o?.[key] : o)).filter((n) => !Number.isNaN(n))

function registerFilters(e: Liquid): void {
  e.registerFilter('money', (v, currency = 'USD', locale = 'en-US') => {
    const n = Number(v)
    return Number.isNaN(n) ? v : new Intl.NumberFormat(locale, { style: 'currency', currency }).format(n)
  })
  e.registerFilter('number', (v, digits, locale) => {
    const n = Number(v)
    if (Number.isNaN(n)) return v
    const opts: Intl.NumberFormatOptions = digits == null ? {} : { minimumFractionDigits: digits, maximumFractionDigits: digits }
    return new Intl.NumberFormat(locale, opts).format(n)
  })
  e.registerFilter('percent', (v, digits = 0, locale) =>
    Number.isNaN(Number(v)) ? v : new Intl.NumberFormat(locale, { style: 'percent', minimumFractionDigits: digits, maximumFractionDigits: digits }).format(Number(v)))
  e.registerFilter('sum', (arr, key) => toNums(arr, key).reduce((a, b) => a + b, 0))
  e.registerFilter('avg', (arr, key) => { const a = toNums(arr, key); return a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0 })
  e.registerFilter('count', (arr) => (Array.isArray(arr) ? arr.length : 0))
  e.registerFilter('min', (arr, key) => { const a = toNums(arr, key); return a.length ? Math.min(...a) : 0 })
  e.registerFilter('max', (arr, key) => { const a = toNums(arr, key); return a.length ? Math.max(...a) : 0 })
}

const engine = new Liquid({ jsTruthy: true }) // no HTML escaping by default
registerFilters(engine)

// Validation engine: strict about unknown filters AND unknown variables, so a
// typo'd filter or field name surfaces as an error instead of silently passing
// through (the lenient engine would render the raw value / fall back to the
// template text — which would ship as visible code in the exported PDF).
const strictEngine = new Liquid({ jsTruthy: true, strictFilters: true, strictVariables: true })
registerFilters(strictEngine)

/** Validate a template against a scope. Returns an error message, or null when OK. */
export function lqError(tpl: unknown, scope: Record<string, any>): string | null {
  if (typeof tpl !== 'string' || (!tpl.includes('{{') && !tpl.includes('{%'))) return null
  try {
    strictEngine.parseAndRenderSync(tpl, scope)
    return null
  } catch (e) {
    return (e as Error).message
  }
}

/** Case-insensitive scope: a {{Name}} template resolves a CSV "name" column and vice-versa. */
export function rowScope(row: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = { ...row }
  for (const [k, v] of Object.entries(row)) out[k.toLowerCase()] = v
  return out
}

/**
 * Literal exact-header pass, run BEFORE Liquid. Spreadsheet headers routinely
 * contain spaces, dots and accents ("P.V.P USD", "URL QR", "CÓDIGO") which are
 * not Liquid identifiers — Liquid throws, lq() would return the template
 * unchanged, and the label PRINTS the raw {{placeholder}} (customer report,
 * 2026-08-23). The classic editor matched headers literally; do that first,
 * case-insensitively, for {{ name }} expressions that equal a scope key. Filter
 * expressions ({{ x | money }}) never equal a header, so Liquid still gets them.
 */
export function substituteLiteralFields(tpl: string, scope: Record<string, any>): string {
  if (!tpl.includes('{{')) return tpl
  const keys = Object.keys(scope)
  return tpl.replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (m, name: string) => {
    const want = name.trim().toLowerCase()
    const key = keys.find((k) => k.trim().toLowerCase() === want)
    if (key === undefined) return m
    const v = scope[key]
    return v == null ? '' : String(v)
  })
}

/** Render a Liquid template string against a scope. Plain strings (no tags) pass through. */
export function lq(tpl: unknown, scope: Record<string, any>): string {
  if (typeof tpl !== 'string') return tpl as any
  if (!tpl.includes('{{') && !tpl.includes('{%')) return tpl
  const pre = substituteLiteralFields(tpl, scope)
  if (!pre.includes('{{') && !pre.includes('{%')) return pre
  try {
    return engine.parseAndRenderSync(pre, scope)
  } catch {
    return pre
  }
}

/** Bound field/dataset name of each {{ ... }} expression: the full inner name
 *  for plain references (so "P.V.P USD" / "CÓDIGO" count as fields), or the
 *  leading identifier before a filter/space-separated expression. */
export function fieldsIn(text: string): string[] {
  const out: string[] = []
  for (const m of text.matchAll(/\{\{\s*([^{}|]+?)\s*(?:\||\}\})/g)) {
    const name = m[1].trim()
    if (name) out.push(name)
  }
  return out
}
